import * as PIXI from 'pixi.js';
import { AudioSystem } from '../assets/AudioSystem';

interface InstructionsOptions {
  initialDontShowAgain?: boolean;
  onDontShowAgainChange?: (dontShow: boolean) => void;
  onClose?: () => void;
  audioSystem?: AudioSystem | null;
}

export class Instructions {
  private container: PIXI.Container;
  private visible: boolean = true;
  private dontShowAgain: boolean;
  private checkbox?: PIXI.Graphics;
  private options: InstructionsOptions;

  constructor(options: InstructionsOptions = {}) {
    this.options = options;
    this.dontShowAgain = !!options.initialDontShowAgain;
    this.container = new PIXI.Container();
    this.setupInstructions();
    this.updateCheckbox();
    this.container.visible = true;
  }

  private setupInstructions(): void {
    const screenWidth = 1920;
    const screenHeight = 1080;

    const backdrop = new PIXI.Graphics();
    backdrop.beginFill(0x000000, 0.88);
    backdrop.drawRect(0, 0, screenWidth, screenHeight);
    backdrop.endFill();
    backdrop.eventMode = 'static';
    this.container.addChild(backdrop);

    const panelWidth = 1000;
    const panelHeight = 800;
    const panelX = (screenWidth - panelWidth) / 2;
    const panelY = (screenHeight - panelHeight) / 2;

    const panel = new PIXI.Graphics();
    panel.beginFill(0x1a1a1a, 0.9);
    panel.drawRoundedRect(0, 0, panelWidth, panelHeight, 12);
    panel.endFill();
    panel.lineStyle(2, 0xffffff, 0.15);
    panel.drawRoundedRect(0, 0, panelWidth, panelHeight, 12);
    panel.x = panelX;
    panel.y = panelY;
    panel.eventMode = 'static';
    this.container.addChild(panel);

    const title = new PIXI.Text('Single Player Briefing', {
      fontFamily: 'Courier New',
      fontSize: 39,
      fill: 0xffffff,
      fontWeight: 'bold',
    });
    title.x = panelX + 24;
    title.y = panelY + 24;
    this.container.addChild(title);

    const scrollArea = new PIXI.Container();
    scrollArea.x = panelX + 24;
    scrollArea.y = panelY + 80;
    this.container.addChild(scrollArea);

    const sections: Array<{ heading: string; body: string }> = [
      {
        heading: 'Towers',
        body:
          'Arrow: Rapid single-target. Cannon: AoE + armor shred. Sniper: Long-range execute.\n' +
          'Upgrades A & B stack. Arrow A: Rapid Fire. Cannon A: Larger radius. Sniper A: True Strike.',
      },
      {
        heading: 'Building',
        body:
          'Walls refund 50% when sold. Damaged buildings (<100% HP) sell for 60% less.\n' +
          'Green preview = valid placement. Red = blocked path.',
      },
      {
        heading: 'Quick Build & Sell',
        body:
          'Quick Build (Settings): Click tower button, then click on walls to auto-replace with towers. Works during waves!\n' +
          'Quick Sell (Settings): Right-click buildings to instantly sell. WARNING: Can waste gold if not careful!',
      },
      {
        heading: 'Economy',
        body:
          'Gold from kills, wave bonuses, and test buttons. Bonuses scale with progress.',
      },
      {
        heading: 'Block Breaker',
        body:
          'Red path: enemies attack closest blocker (+30% speed). Green path: reroute or phase through if stuck.\n' +
          'Ghost/Flying units ignore block breaker.',
      },
    ];

    let currentY = 0;
    const headingStyle = new PIXI.TextStyle({
      fontFamily: 'Courier New',
      fontSize: 26,
      fill: 0xffe082,
      fontWeight: 'bold',
    });
    const bodyStyle = new PIXI.TextStyle({
      fontFamily: 'Courier New',
      fontSize: 21,
      fill: 0xffffff,
      wordWrap: true,
      wordWrapWidth: panelWidth - 64,
      lineHeight: 28,
    });

    for (const section of sections) {
      const heading = new PIXI.Text(section.heading, headingStyle);
      heading.y = currentY;
      scrollArea.addChild(heading);
      currentY += heading.height + 6;

      const body = new PIXI.Text(section.body, bodyStyle);
      body.y = currentY;
      scrollArea.addChild(body);
      currentY += body.height + 20;
    }

    const instructionsFooter = new PIXI.Text(
      'Build your maze, then Start Wave. Press Esc for Settings.',
      {
        fontFamily: 'Courier New',
        fontSize: 18,
        fill: 0x9bd4ff,
        wordWrap: true,
        wordWrapWidth: panelWidth - 64,
      }
    );
    instructionsFooter.y = currentY;
    scrollArea.addChild(instructionsFooter);

    // Checkbox
    this.checkbox = new PIXI.Graphics();
    this.checkbox.eventMode = 'static';
    this.checkbox.cursor = 'pointer';
    this.checkbox.on('pointerdown', () => {
      this.dontShowAgain = !this.dontShowAgain;
      this.updateCheckbox();
      this.options.onDontShowAgainChange?.(this.dontShowAgain);
    });
    this.checkbox.x = panelX + 24;
    this.checkbox.y = panelY + panelHeight - 70;
    this.container.addChild(this.checkbox);

    const checkboxLabel = new PIXI.Text("Don't show again", {
      fontFamily: 'Courier New',
      fontSize: 21,
      fill: 0xffffff,
    });
    checkboxLabel.x = this.checkbox.x + 32;
    checkboxLabel.y = this.checkbox.y - 2;
    this.container.addChild(checkboxLabel);

    // Close button
    const closeButton = new PIXI.Graphics();
    closeButton.beginFill(0x00a86b);
    closeButton.drawRoundedRect(0, 0, 200, 44, 8);
    closeButton.endFill();
    closeButton.x = panelX + panelWidth - 224;
    closeButton.y = panelY + panelHeight - 82;
      closeButton.eventMode = 'static';
      closeButton.cursor = 'pointer';
      closeButton.on('pointerdown', () => {
        this.options.audioSystem?.playSound('menu_single_player');
        this.hide();
      });
      this.container.addChild(closeButton);

    const closeText = new PIXI.Text('Enter the Arena', {
      fontFamily: 'Courier New',
      fontSize: 23,
      fill: 0xffffff,
      fontWeight: 'bold',
    });
    closeText.x = closeButton.x + closeButton.width / 2 - closeText.width / 2;
    closeText.y = closeButton.y + closeButton.height / 2 - closeText.height / 2;
    this.container.addChild(closeText);
  }

  private updateCheckbox(): void {
    if (!this.checkbox) return;
    this.checkbox.clear();
    this.checkbox.beginFill(this.dontShowAgain ? 0x00ff88 : 0x333333);
    this.checkbox.drawRoundedRect(0, 0, 24, 24, 4);
    this.checkbox.endFill();
    this.checkbox.lineStyle(2, 0xffffff, 0.7);
    this.checkbox.drawRoundedRect(0, 0, 24, 24, 4);
  }

  hide(): void {
    this.visible = false;
    this.container.visible = false;
    this.options.onClose?.();
  }

  show(): void {
    this.visible = true;
    this.container.visible = true;
  }

  isVisible(): boolean {
    return this.visible;
  }

  getContainer(): PIXI.Container {
    return this.container;
  }

  setDontShowAgain(value: boolean): void {
    this.dontShowAgain = value;
    this.updateCheckbox();
  }
}

